import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Brain, Plus, Edit, Trash2, Eye, EyeOff, Search, RefreshCcw,
  Users, BarChart2, Clock, FileText, CheckCircle, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PsychologyTestsManagement = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, completions: 0 });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    test_type: 'personality',
    duration_minutes: 20,
    question_count: 30,
    is_active: true
  });

  useEffect(() => {
    loadTests();
    loadStats();
  }, []);

  const loadTests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/psychology-tests`, { withCredentials: true });
      setTests(response.data.tests || []);
    } catch (error) {
      // Demo data
      setTests([
        { id: '1', title: 'Kişilik Envanteri (Big Five)', test_type: 'personality', duration_minutes: 25, question_count: 50, completion_count: 234, is_active: true },
        { id: '2', title: 'Duygusal Zeka Testi', test_type: 'emotional', duration_minutes: 20, question_count: 40, completion_count: 187, is_active: true },
        { id: '3', title: 'Stres Yönetimi Değerlendirmesi', test_type: 'stress', duration_minutes: 15, question_count: 25, completion_count: 156, is_active: true },
        { id: '4', title: 'İletişim Becerileri Analizi', test_type: 'communication', duration_minutes: 18, question_count: 35, completion_count: 98, is_active: false },
        { id: '5', title: 'Takım Çalışması Uyumluluk Testi', test_type: 'teamwork', duration_minutes: 20, question_count: 30, completion_count: 145, is_active: true }
      ]);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/psychology-tests/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      setStats({ total: 5, active: 4, completions: 820 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTest) {
        await axios.put(`${API}/admin/psychology-tests/${editingTest.id}`, formData, { withCredentials: true });
        toast.success('Test güncellendi');
      } else {
        await axios.post(`${API}/admin/psychology-tests`, formData, { withCredentials: true });
        toast.success('Test oluşturuldu');
      }
      setShowModal(false);
      resetForm();
      loadTests();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu testi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/psychology-tests/${id}`, { withCredentials: true });
      toast.success('Test silindi');
      loadTests();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/psychology-tests/${id}/toggle`, {}, { withCredentials: true });
      toast.success(currentStatus ? 'Pasif yapıldı' : 'Aktif yapıldı');
      loadTests();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      test_type: 'personality',
      duration_minutes: 20,
      question_count: 30,
      is_active: true
    });
    setEditingTest(null);
  };

  const handleEdit = (test) => {
    setEditingTest(test);
    setFormData({
      title: test.title || '',
      description: test.description || '',
      test_type: test.test_type || 'personality',
      duration_minutes: test.duration_minutes || 20,
      question_count: test.question_count || 30,
      is_active: test.is_active
    });
    setShowModal(true);
  };

  const getTypeBadge = (type) => {
    const badges = {
      'personality': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Kişilik', icon: '🧠' },
      'emotional': { bg: 'bg-pink-100', text: 'text-pink-700', label: 'Duygusal Zeka', icon: '❤️' },
      'stress': { bg: 'bg-red-100', text: 'text-red-700', label: 'Stres', icon: '😰' },
      'communication': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'İletişim', icon: '💬' },
      'teamwork': { bg: 'bg-green-100', text: 'text-green-700', label: 'Takım Çalışması', icon: '👥' },
      'aptitude': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Yetenek', icon: '🎯' }
    };
    return badges[type] || badges['personality'];
  };

  const filteredTests = tests.filter(t => 
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.test_type?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-pink-600 rounded-lg text-white">
            <Brain className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Psikoloji Testleri Yönetimi</h2>
            <p className="text-sm text-gray-500">{stats.total} test, {stats.completions} tamamlama</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="gap-2 bg-pink-600 hover:bg-pink-700">
          <Plus className="w-4 h-4" />
          Yeni Test
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow bg-gradient-to-br from-pink-50 to-pink-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Brain className="w-8 h-8 text-pink-600" />
              <div>
                <p className="text-2xl font-bold text-pink-700">{stats.total}</p>
                <p className="text-xs text-pink-600">Toplam Test</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.active}</p>
                <p className="text-xs text-green-600">Aktif Test</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.completions}</p>
                <p className="text-xs text-blue-600">Toplam Tamamlama</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart2 className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">
                  {tests.reduce((sum, t) => sum + (t.question_count || 0), 0)}
                </p>
                <p className="text-xs text-amber-600">Toplam Soru</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-0 shadow">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Test ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={loadTests}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tests List */}
      <Card className="border-0 shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCcw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : filteredTests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Test bulunamadı</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredTests.map((test) => {
                const typeBadge = getTypeBadge(test.test_type);
                return (
                  <div key={test.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{typeBadge.icon}</span>
                          <h3 className={`font-medium ${test.is_active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                            {test.title}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeBadge.bg} ${typeBadge.text}`}>
                            {typeBadge.label}
                          </span>
                          {!test.is_active && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Pasif</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {test.duration_minutes} dk
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {test.question_count} soru
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {test.completion_count || 0} tamamlama
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(test.id, test.is_active)}>
                          {test.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(test)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(test.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingTest ? 'Testi Düzenle' : 'Yeni Psikoloji Testi'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Test Adı *</label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Test başlığı"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border rounded-md resize-none h-20"
                    placeholder="Test açıklaması..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Test Türü</label>
                  <select
                    value={formData.test_type}
                    onChange={(e) => setFormData({...formData, test_type: e.target.value})}
                    className="w-full h-10 px-3 border rounded-md"
                  >
                    <option value="personality">Kişilik Testi</option>
                    <option value="emotional">Duygusal Zeka</option>
                    <option value="stress">Stres Yönetimi</option>
                    <option value="communication">İletişim Becerileri</option>
                    <option value="teamwork">Takım Çalışması</option>
                    <option value="aptitude">Yetenek Testi</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Süre (dakika)</label>
                    <Input
                      type="number"
                      min="5"
                      max="120"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Soru Sayısı</label>
                    <Input
                      type="number"
                      min="5"
                      max="200"
                      value={formData.question_count}
                      onChange={(e) => setFormData({...formData, question_count: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                    İptal
                  </Button>
                  <Button type="submit" className="bg-pink-600 hover:bg-pink-700">
                    {editingTest ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PsychologyTestsManagement;
